import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import axios from 'axios';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { getApiBaseUrl, getStoredToken } from '../../services/httpClient';

const API_BASE_URL = getApiBaseUrl();
const getRequestHeaders = () => {
  const token = getStoredToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

const validationSchema = yup.object({
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[6-9]\d{9}$/, 'Invalid phone number (10 digits)'),
  email: yup.string().email('Invalid email address'),
  full_name: yup.string().required('Full name is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const OperatorManagement = () => {
  const [operators, setOperators] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load operators
      const operatorsResponse = await axios.get(
        `${API_BASE_URL}/franchisee/operator/list`,
        {
          headers: getRequestHeaders(),
        }
      );

      // Load available permissions
      const permissionsResponse = await axios.get(
        `${API_BASE_URL}/franchisee/operator/available-permissions`,
        {
          headers: getRequestHeaders(),
        }
      );

      if (operatorsResponse.data.success) {
        setOperators(operatorsResponse.data.data || []);
      }

      if (permissionsResponse.data.success) {
        setAvailablePermissions(permissionsResponse.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setIsEditing(false);
    setSelectedOperator(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOperator(null);
    setIsEditing(false);
  };

  const handleOpenPermissionDialog = (operator) => {
    setSelectedOperator(operator);
    const grantedPermissions = operator.permissions
      ?.filter((p) => p.is_granted)
      .map((p) => p.permission_name) || [];
    setSelectedPermissions(grantedPermissions);
    setPermissionDialogOpen(true);
  };

  const handleClosePermissionDialog = () => {
    setPermissionDialogOpen(false);
    setSelectedOperator(null);
    setSelectedPermissions([]);
  };

  const handlePermissionToggle = (permission) => {
    if (selectedPermissions.includes(permission)) {
      setSelectedPermissions(selectedPermissions.filter((p) => p !== permission));
    } else {
      setSelectedPermissions([...selectedPermissions, permission]);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedOperator) return;

    try {
      const response = await axios.put(
        `${API_BASE_URL}/franchisee/operator/${selectedOperator.id}/permissions`,
        {
          permissions: selectedPermissions,
        },
        {
          headers: getRequestHeaders(),
        }
      );

      if (response.data.success) {
        setSuccess('Permissions updated successfully');
        handleClosePermissionDialog();
        loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to update permissions');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update permissions');
      console.error('Error updating permissions:', err);
    }
  };

  const formik = useFormik({
    initialValues: {
      phone: '',
      email: '',
      full_name: '',
      password: '',
      permissions: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setError('');
      setSuccess('');

      try {
        const response = await axios.post(
          `${API_BASE_URL}/franchisee/operator/create`,
          {
            phone: values.phone,
            email: values.email,
            full_name: values.full_name,
            password: values.password,
            permissions: values.permissions,
          },
          {
            headers: getRequestHeaders(),
          }
        );

        if (response.data.success) {
          setSuccess('Operator created successfully');
          handleCloseDialog();
          formik.resetForm();
          loadData();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(response.data.message || 'Failed to create operator');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to create operator');
        console.error('Error creating operator:', err);
      }
    },
  });

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Operator Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage franchisee operators
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            Create Operator
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card>
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {operators.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No operators found. Create your first operator.
                        </TableCell>
                      </TableRow>
                    ) : (
                      operators.map((operator) => {
                        const grantedPermissions = operator.permissions
                          ?.filter((p) => p.is_granted)
                          .map((p) => p.permission_name) || [];

                        return (
                          <TableRow key={operator.id}>
                            <TableCell>{operator.phone}</TableCell>
                            <TableCell>{operator.email || 'N/A'}</TableCell>
                            <TableCell>{operator.full_name}</TableCell>
                            <TableCell>
                              <Chip
                                label={operator.status}
                                color={operator.status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {grantedPermissions.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {grantedPermissions.map((permission) => (
                                    <Chip
                                      key={permission}
                                      label={permission}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No permissions
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {operator.last_login
                                ? new Date(operator.last_login).toLocaleString()
                                : 'Never'}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => handleOpenPermissionDialog(operator)}
                              >
                                Edit Permissions
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Create Operator Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Operator</DialogTitle>
          <form onSubmit={formik.handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email (Optional)"
                    name="email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="full_name"
                    value={formik.values.full_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                    helperText={formik.touched.full_name && formik.errors.full_name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Initial Permissions (can be changed later):
                  </Typography>
                  {availablePermissions.map((permission) => (
                    <FormControlLabel
                      key={permission}
                      control={
                        <Checkbox
                          checked={formik.values.permissions.includes(permission)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              formik.setFieldValue('permissions', [
                                ...formik.values.permissions,
                                permission,
                              ]);
                            } else {
                              formik.setFieldValue(
                                'permissions',
                                formik.values.permissions.filter((p) => p !== permission)
                              );
                            }
                          }}
                        />
                      }
                      label={permission}
                    />
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained">
                Create
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Edit Permissions Dialog */}
        <Dialog
          open={permissionDialogOpen}
          onClose={handleClosePermissionDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Edit Permissions - {selectedOperator?.full_name}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the permissions to grant to this operator:
            </Typography>
            <Grid container spacing={2}>
              {availablePermissions.map((permission) => (
                <Grid item xs={12} key={permission}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedPermissions.includes(permission)}
                        onChange={() => handlePermissionToggle(permission)}
                      />
                    }
                    label={permission}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePermissionDialog}>Cancel</Button>
            <Button onClick={handleSavePermissions} variant="contained">
              Save Permissions
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default OperatorManagement;















